import { useEffect } from "react";
import { Link } from "react-router-dom";
import { X, FlaskConical } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { api, photoUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PopupSignup } from "./PopupSignup";
import { useAutoClose, AutoCloseBar } from "./popupAutoClose";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";

export const track = (p, kind) => { if (p?._page) api.post("/site/popup/event", { page: p._page, ab_id: p._ab || "", variant: p._v || "", kind }).catch(() => {}); };

const BTN = "inline-flex items-center justify-center h-11 px-6 rounded-full font-semibold transition-colors";
export const BTN_COLORS = {
  orange: "bg-[#F5551E] hover:bg-[#e04812] text-white",
  navy: "bg-[#12234A] hover:bg-[#0c1834] text-white",
  white: "bg-white hover:bg-slate-50 text-[#12234A] border border-slate-200",
};

const CtaButton = ({ p, n = 1, pick, onClose, preview, test, full = false, overlay = false }) => {
  const { user, setAuthOpen } = useAuth();
  const k = n === 2 ? "button2" : "button";
  const link = p[`${k}_link`] || "";
  const label = pick(`${k}_text`);
  if (!label || !link) return null;
  const color = BTN_COLORS[p[`${k}_color`]] || BTN_COLORS[n === 2 ? "white" : "orange"];
  const cls = `${BTN} ${color} ${full ? "flex-1" : ""} ${overlay ? "shadow-lg" : ""}`;
  const tid = n === 2 ? "home-popup-button-2" : "home-popup-button";
  const needsLogin = !user && /^\/(post|dashboard|advertise|edit)(\/|\?|$)/.test(link);
  const go = (e) => {
    if (test) { e.preventDefault(); toast.info(`Test mode — this button goes to ${link}`); return; }
    track(p, "click");
    if (needsLogin) { e.preventDefault(); setAuthOpen(true); }
    onClose();
  };
  if (preview && !test) return <span className={cls} data-testid={`${tid}-preview`}>{label}</span>;
  return link.startsWith("/")
    ? <Link to={link} onClick={go} className={cls} data-testid={tid}>{label}</Link>
    : <a href={link} target="_blank" rel="noopener noreferrer" onClick={go} className={cls} data-testid={tid}>{label}</a>;
};

const Buttons = (props) => (
  <>
    <CtaButton {...props} n={1} />
    <CtaButton {...props} n={2} />
  </>
);

const OVERLAY = { top: "top-4", bottom: "bottom-4", left: "left-4 justify-start", center: "inset-x-4 justify-center", right: "right-4 justify-end" };
const ALIGN = { left: "justify-start", center: "justify-center", right: "justify-end", full: "" };

export const PopupCard = ({ p, lang, onClose, onSignup, preview = false, test = false, bar = false }) => {
  const pick = (k) => (lang === "fr" ? p[`${k}_fr`] || p[k] : p[k]);
  const close = (
    <button onClick={onClose} aria-label="Close" data-testid="home-popup-close" className="absolute top-3 right-3 z-10 h-9 w-9 rounded-full bg-white/90 hover:bg-white shadow flex items-center justify-center text-[#12234A] transition-colors">
      <X size={18} />
    </button>
  );
  const text = (
    <>
      {pick("title") && <h2 className={`font-display font-bold text-[#12234A] pr-8 ${bar ? "text-xl" : "text-2xl"}`} data-testid="home-popup-title">{pick("title")}</h2>}
      {pick("body") && <p className={`text-slate-600 whitespace-pre-line leading-relaxed ${bar ? "mt-1 text-sm" : "mt-2"}`} data-testid="home-popup-body">{pick("body")}</p>}
    </>
  );
  const signup = p.signup_enabled && <PopupSignup key={lang} p={p} lang={lang} preview={preview} onDone={onSignup} />;
  if (bar) {
    return (
      <div className="relative max-w-6xl mx-auto flex flex-wrap sm:flex-nowrap items-center gap-5 pl-4 sm:pl-6 pr-14 py-4" data-testid="home-popup-card">
        {close}
        {p.image && <img src={photoUrl(p.image)} alt="" data-testid="home-popup-image" className="hidden sm:block h-20 w-28 rounded-xl object-cover shrink-0" />}
        <div className="flex-1 min-w-0">{text}</div>
        <div className="shrink-0 flex gap-2"><Buttons p={p} pick={pick} onClose={onClose} preview={preview} test={test} /></div>
        {signup && <div className="w-full sm:w-80 shrink-0 [&_form]:mt-0 [&>p]:mt-0">{signup}</div>}
      </div>
    );
  }
  const hasBtn = (pick("button_text") && p.button_link) || (pick("button2_text") && p.button2_link);
  const [where, a, b] = (p.button_position || "below-left").split("-");
  const overlay = hasBtn && where === "image" && p.image;
  const align = where === "image" ? b : a;
  const v = where === "image" ? a : "";
  return (
    <div className="relative" data-testid="home-popup-card">
      {close}
      {p.image && (
        <div className="relative">
          <img src={photoUrl(p.image)} alt="" data-testid="home-popup-image" className="w-full object-cover" style={{ maxHeight: `${p.image_height || 320}px` }} />
          {overlay && (
            <div className={`absolute flex flex-wrap gap-2 ${OVERLAY[v]} ${OVERLAY[align]} ${v === "top" && align !== "left" ? "pr-12" : ""}`} data-testid="home-popup-button-overlay" data-button-position={p.button_position}>
              <Buttons p={p} pick={pick} onClose={onClose} preview={preview} test={test} overlay />
            </div>
          )}
        </div>
      )}
      <div className="p-6 sm:p-7">
        {text}
        {hasBtn && !overlay && (
          <div className={`mt-5 flex flex-wrap gap-2 ${ALIGN[align] ?? ""}`} data-testid="home-popup-button-row" data-button-position={p.button_position || "below-left"}>
            <Buttons p={p} pick={pick} onClose={onClose} preview={preview} test={test} full={align === "full"} />
          </div>
        )}
        {signup}
      </div>
    </div>
  );
};

const TestBadge = () => (
  <div className="flex items-center gap-1.5 bg-[#12234A] text-white text-[11px] font-semibold px-3 py-1.5" data-testid="popup-test-badge">
    <FlaskConical size={12} /> Admin test — only you can see this. Sign-ups and clicks aren't saved.
  </div>
);

const CORNER = {
  "bottom-right": "bottom-4 right-4", "bottom-left": "bottom-4 left-4", "top-right": "top-[8.5rem] right-4", "top-left": "top-[8.5rem] left-4",
};

// Renders a popup as a centred dialog, a corner slide-in or a bottom bar
export function PopupShell({ p, open, onClose, onSignup, lang, page, test = false }) {
  const pos = p.position || "center";
  useEffect(() => {
    if (!open || pos === "center") return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, pos, onClose]);
  const card = (bar) => <PopupCard p={p} lang={lang} onClose={onClose} onSignup={onSignup} preview={test} test={test} bar={bar} />;
  const ac = useAutoClose(p.auto_close_seconds || 0, open, onClose);
  const countdown = ac.enabled && <AutoCloseBar left={ac.left} paused={ac.paused} />;

  if (pos === "center") {
    return (
      <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
        <DialogContent {...ac.handlers} className="p-0 overflow-y-auto max-h-[92vh] w-[calc(100%-2rem)] rounded-2xl border-0 [&>button]:hidden" style={{ maxWidth: `${p.width_px || 512}px` }} data-testid="home-popup" data-page={page} data-position="center">
          {countdown}
          <DialogTitle className="sr-only">{p.title || "CritterPosts"}</DialogTitle>
          <DialogDescription className="sr-only">{p.body || p.title}</DialogDescription>
          {test && <TestBadge />}
          {card(false)}
          {p._v && <span className="hidden" data-testid="home-popup-variant">{p._v}</span>}
        </DialogContent>
      </Dialog>
    );
  }
  const bar = pos === "bottom-bar";
  const fromLeft = pos.endsWith("left");
  const anim = bar ? { initial: { y: "100%" }, animate: { y: 0 }, exit: { y: "100%" } }
    : { initial: { opacity: 0, x: fromLeft ? -40 : 40 }, animate: { opacity: 1, x: 0 }, exit: { opacity: 0, x: fromLeft ? -40 : 40 } };
  return (
    <AnimatePresence>
      {open && (
        <motion.div key="popup" {...anim} transition={{ type: "spring", stiffness: 260, damping: 28 }} role="dialog" aria-label={p.title || "CritterPosts"}
          data-testid="home-popup" data-page={page} data-position={pos}
          className={bar
            ? "fixed inset-x-0 bottom-0 z-[70] bg-white border-t border-slate-200 shadow-[0_-10px_40px_rgba(18,35,74,0.18)] max-h-[70vh] overflow-y-auto"
            : `fixed z-[70] ${CORNER[pos]} w-[calc(100%-2rem)] ${pos.startsWith("top") ? "max-h-[calc(100vh-10rem)]" : "max-h-[calc(100vh-6rem)]"} overflow-y-auto bg-white rounded-2xl shadow-2xl border border-slate-100`}
          style={bar ? undefined : { maxWidth: `${p.width_px || 512}px` }} {...ac.handlers}>
          {countdown}
          {test && <TestBadge />}
          {card(bar)}
          {p._v && <span className="hidden" data-testid="home-popup-variant">{p._v}</span>}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
